import {
	Component,
	computed,
	DestroyRef,
	inject,
	signal,
	type OnDestroy,
	type OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import {
	debounceTime,
	distinctUntilChanged,
	finalize,
	firstValueFrom,
	of,
	Subject,
	switchMap
} from 'rxjs';
import * as XLSX from 'xlsx';
import {
	type Quote,
	type Attachment,
	type ThreadReply
} from '../../models/quote.model';
import { type InsideSalesRep } from '../../models/inside-sales-rep';
import { QuoteHttpService } from '../../services/quote-http.service';
import { type NsCustomer } from '../../models/ns-customer';
import { type QuoteFilters } from '../../models/quote-filters';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule, type DatePicker } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import {
	type AttachmentExtractionInfo,
	type ItemOption,
	type QuoteItemMatch,
	type QuoteItemMatchSaveRow
} from '../../models/quote-item-match';
import { ToastService } from '../../services/toast.service';
import { ViewportService } from '../../services/viewport.service';
import { EmbarkSpinnerComponent } from '../embark-spinner/embark-spinner.component';
import { QuoteFiltersComponent } from './quote-filters/quote-filters.component';
import { QuoteLogExportService } from './quote-export/quote-log-export.service';
import {
	createEmptyQuoteFilters,
	filterQuotes
} from './quote-filters/quote-filters.helpers';
import {
	calcHours,
	calcIsOverdue,
	CREDIT_CARD_SALE_NAME,
	customerIsDefaultOrEmpty,
	nsCustomerFields,
	selectedNsRecordId,
	toIsoUtc,
	toQuote
} from './quote-helpers';
import { environment } from '../../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { resolveActiveAccount } from '../../guards/active-account';
import { userHasSalesManagerAccess } from '../../guards/quote-log.guard';

@Component({
	selector: 'app-quote-log',
	standalone: true,
	imports: [
		FormsModule,
		DatePipe,
		TableModule,
		ButtonModule,
		InputTextModule,
		SelectModule,
		TagModule,
		DialogModule,
		TooltipModule,
		DatePickerModule,
		CheckboxModule,
		ConfirmDialogModule,
		EmbarkSpinnerComponent,
		QuoteFiltersComponent
	],
	templateUrl: './quote-log.component.html',
	styleUrls: ['./quote-log.component.sass'],
	providers: [ConfirmationService]
})
export class QuoteLogComponent implements OnInit, OnDestroy {
	private quoteService = inject(QuoteHttpService);
	private toastService = inject<ToastService>(ToastService);
	private confirmationService = inject<ConfirmationService>(ConfirmationService);
	private msalService: MsalService = inject<MsalService>(MsalService);
	private quoteLogExportService = inject(QuoteLogExportService);
	public readonly viewport = inject(ViewportService);

	//holds all, unfiltered quotes
	public readonly allQuotes = signal<Quote[]>([]);

	//filtered quotes that the table renders.
	public quotes = signal<Quote[]>([]);
	public pageSize = signal(50);

	// Mobile card view pagination
	public cardPageSize = 15;
	public currentCardPage = signal(0);
	public paginatedQuotes = computed(() => {
		const start = this.currentCardPage() * this.cardPageSize;
		const end = start + this.cardPageSize;
		return this.quotes().slice(start, end);
	});
	public totalCardPages = computed(
		() => Math.ceil(this.quotes().length / this.cardPageSize)
	);

	// How long an inside rep pick sits as "pending" (with an Undo affordance in the UI)
	// before we commit it and create the NetSuite estimate — creating/recreating an
	// estimate is expensive to reverse, so the user gets a window to correct a misclick.
	private readonly isrAssignmentUndoWindowMs = 2500;

	// NetSuite needs a moment to index a newly created estimate before its status
	// is queryable, so the status refresh after submission waits this long first.
	private readonly nsStatusPropagationDelayMs = 2000;
	private pendingIsrTimers = new Map<string, ReturnType<typeof setTimeout>>();

	public insideSalesReps = signal<InsideSalesRep[]>([]);
	public selectedQuote = signal<Quote | null>(null);
	public showEmailModal = signal(false);
	public showDetailPopover = signal(false);
	public selectedDetailQuote = signal<Quote | null>(null);
	public nsCustomers = signal<NsCustomer[]>([]);
	public showExportDialog = signal(false);
	public exportStartDate: Date | null = null;
	public exportEndDate: Date | null = null;

	// --- Item matching (the detail dialog's right panel) ---

	public itemMatches = signal<QuoteItemMatch[]>([]);
	public itemMatchesLoading = signal(false);
	public isExtracting = signal(false);
	public isExporting = signal(false);
	public attachmentInfos = signal<AttachmentExtractionInfo[]>([]);
	public basketUnavailable = signal(false);
	public basketError = signal<string | null>(null);
	public matchSourceList = signal<string>('standard');
	public lowConfidenceThreshold = signal(75);
	public customerNotes = signal<string | null>(null);

	// Market basket customers get their whole basket up front; standard customers
	// can't (the catalog is ~50k items) so their dropdown queries as the user types.
	public useItemSearch = signal(false);
	public basketOptions = signal<ItemOption[]>([]);
	private searchResults = signal<ItemOption[]>([]);
	private searchingRowOrder = signal<number | null>(null);
	private readonly itemSearch$ = new Subject<{
		quoteId: number;
		query: string;
	}>();

	private destroyRef = inject(DestroyRef);

	// Latest criteria emitted by the filter bar. Held here so the parent can
	// re-filter after local mutations without the child re-emitting.
	private readonly filters = signal<QuoteFilters>(createEmptyQuoteFilters());

	public isSyncing = signal(false);

	// Starts true so the very first render shows a spinner instead of briefly
	// claiming "No quotes found" before the initial fetch resolves.
	public isLoadingQuotes = signal(true);

	// The initial NS status sync also refetches the log, so an in-flight sync
	// still counts as "quotes are being retrieved".
	public isLoading = computed(
		() => this.isLoadingQuotes() || this.isSyncing()
	);

	public editingDueDateId = signal<string | null>(null);
	public editingDueDateValue = signal<Date | null>(null);
	// private editingDueDateOriginal: Date | null = null;

	public userIsSalesManager = false;

	public ngOnInit(): void {
		this.loadQuotes();
		this.loadInsideSalesReps();
		this.loadCustomers();
		this.syncStatuses();
		this.wireItemSearch();
		this.setUserIsSalesManager();
	}

	public ngOnDestroy(): void {
		this.itemSearch$.complete();
		for (const timerId of this.pendingIsrTimers.values()) {
			clearTimeout(timerId);
		}
		this.pendingIsrTimers.clear();
	}

	public setUserIsSalesManager(): void {
		this.userIsSalesManager = userHasSalesManagerAccess(
			resolveActiveAccount(this.msalService)
		);
		if (!environment.production) {
			console.log('user is sales manager: ' + this.userIsSalesManager);
		}
	}

	// Debounced so typing in the part-code filter doesn't fire a request per keystroke,
	// and switchMap so a slow earlier response can't overwrite a newer one.
	private wireItemSearch(): void {
		this.itemSearch$
			.pipe(
				debounceTime(250),
				distinctUntilChanged(
					(a, b) => a.quoteId === b.quoteId && a.query === b.query
				),
				switchMap(({ quoteId, query }) =>
					query.trim().length === 0
						? of([] as ItemOption[])
						: this.quoteService.searchItems(quoteId, query)
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: (options) => {
					this.searchResults.set(options);
				},
				error: () => {
					this.searchResults.set([]);
				}
			});
	}

	private loadQuotes(): void {
		this.isLoadingQuotes.set(true);
		this.quoteService
			.getQuoteLog()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (entries) => {
				this.allQuotes.set(entries.map((e) => toQuote(e)));
				this.applyFilters();
				this.isLoadingQuotes.set(false);
			},
			error: () => {
				this.quotes.set([]);
				this.isLoadingQuotes.set(false);
				console.error('Failed to load quote log');
				this.toastService.showErrorToast('Failed to load quotes');
			}
		});
	}

	private loadInsideSalesReps(): void {
		this.quoteService
			.getInsideSalesReps()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (reps) => {
				this.insideSalesReps.set(reps);
			},
			error: () => {
				console.error('Failed to load inside sales reps');
			}
		});
	}

	// Only feeds the customer dropdown now. The match itself is made by the API
	// as the email arrives, so the quote already has a customer by the time it
	// gets here — all this screen does is let the user correct a bad match.
	private loadCustomers(): void {
		this.quoteService
			.getCustomers()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (customers) => {
				this.nsCustomers.set(customers);
			},
			error: () => {
				console.error('Failed to load NS customers');
			}
		});
	}

	public openEmailModal(quote: Quote): void {
		this.getQuoteThreadReplies(quote);

		this.selectedQuote.set(quote);
		this.showEmailModal.set(true);
	}

	public onFiltersApplied(filters: QuoteFilters): void {
		this.filters.set(filters);
		this.applyFilters();
	}

	private applyFilters(): void {
		this.quotes.set(filterQuotes(this.allQuotes(), this.filters()));
	}

	//
	public setInsideRep(quoteId: string, rep: InsideSalesRep | null): void {
		this.cancelPendingIsrAssignment(quoteId);

		if (!rep) {
			this.updateQuote(quoteId, {
				insideRep: null,
				insideRepId: null,
				status: 'unassigned',
				pendingIsrRep: null
			});
			this.quoteService
				.updateInsideRep(Number(quoteId), '', '')
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: () => {
						this.toastService.showSuccessToast("ISR updated successfully");
					},
					error: () => {
						this.toastService.showErrorToast('Failed to update inside rep');
						console.log('Failed to clear inside rep assignment');
					}
				});

			return;
		}

		//wait 2500ms to allow the user to back out their ISR choice
		const timerId = setTimeout(() => {
			this.pendingIsrTimers.delete(quoteId);
			void this.commitInsideRepAssignment(quoteId, rep);
		}, this.isrAssignmentUndoWindowMs);
		this.pendingIsrTimers.set(quoteId, timerId);
	}

	public undoInsideRepAssignment(quoteId: string): void {
		this.cancelPendingIsrAssignment(quoteId);
		this.updateQuote(quoteId, { pendingIsrRep: null });
	}

	private cancelPendingIsrAssignment(quoteId: string): void {
		const timerId = this.pendingIsrTimers.get(quoteId);
		if (timerId) {
			clearTimeout(timerId);
			this.pendingIsrTimers.delete(quoteId);
		}
	}

	private async commitInsideRepAssignment(
		quoteId: string,
		rep: InsideSalesRep
	): Promise<void> {
		const quote = this.allQuotes().find((q) => q.id === quoteId);

		if (!quote) {
			if (!environment.production) {
				console.log('no quote found to commit inside rep to');
			}
			return;
		}

		this.updateQuote(quoteId, { isEstimateCreating: true });

		// Awaited, not fired and forgotten. The API emails the ISR as part of the
		// submission below and reads the address off this record, so letting the two
		// calls race meant the email could go out before the address was saved.
		try {
			await firstValueFrom(
				this.quoteService.updateInsideRep(
					Number(quoteId),
					rep.id,
					rep.fullName,
					rep.email ?? undefined
				)
			);
		} catch {
			// Not fatal: the address is sent with the submission too, and the API
			// falls back to looking it up in NetSuite.
			console.error('Failed to save inside rep assignment');
		}

		this.quoteService
			.autoSubmitEstimate(Number(quoteId), {
				...(quote.nsEstimateId
					? { existingNsEstimateId: quote.nsEstimateId }
					: {}),
				customerId: quote.nsCustomerId ?? '',
				locationId: quote.nsLocationId ?? '',
				insideRepId: rep.id,
				insideRepName: rep.fullName,
				// Sent with the submission so the assignment email never depends
				// on the call above having landed first.
				...(rep.email ? { insideRepEmail: rep.email } : {}),
				outsideRep: quote.outsideRep.name,
				subject: quote.subject ?? quote.emailSummary,
				dueDate: quote.dueDate,
				receivedDate: quote.receivedDate
			})
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (res) => {
					if (!res.success) {
						this.updateQuote(quoteId, {
							isEstimateCreating: false
						});
						console.error('[AutoSubmit] Failed:', res.error);
						this.toastService.showErrorToast(
							'Quote submission failed'
						);
						return;
					}

					this.updateQuote(quoteId, {
						insideRep: rep.fullName,
						insideRepId: rep.id,
						status: 'assigned',
						pendingIsrRep: null,
						estimateNumber: res.estimateNumber ?? null,
						nsEstimateId: res.estimateId ?? null,
						isEstimateCreating: false
					});

					// The quote did submit, so this is not a submission failure — but the rep does
					// not know they have it, and someone has to tell them. The API has already
					// raised it as critical; this is so whoever is at the screen finds out too.
					if (res.isrEmailSent === false) {
						console.error(
							'[AutoSubmit] ISR not emailed:',
							res.isrEmailError
						);
						this.toastService.showErrorToast(
							res.isrEmailError ??
								'Quote submitted, but the inside sales rep could not be emailed.'
						);
					}

					if (res.estimateId) {
						// Fire-and-forget: the refresh waits out the NS propagation
						// delay and handles its own failures, and nothing after this
						// depends on it.
						void this.refreshQuoteFromServer(quoteId);
						return;
					}
				},
				error: (err) => {
					this.updateQuote(quoteId, { isEstimateCreating: false });
					console.error('Failed to auto-submit estimate');
					if (!environment.production) {
						console.log('[AutoSubmit] failure details:', err);
					}
					this.toastService.showErrorToast('Failed to auto-submit estimate');
					return;
				}
			});
	}

	// Pulls the authoritative row back from the API and merges all of it into
	// local state, rather than hand-picking a few fields — the API resolves
	// more than just NS status during submission (e.g. the ISR email fallback,
	// due date defaults), and cherry-picking fields here left the UI showing
	// stale data for anything not explicitly copied over, only fixed by a full
	// page reload. Delayed because NetSuite needs a moment to index a newly
	// created estimate before its status is queryable.
	private async refreshQuoteFromServer(quoteId: string): Promise<void> {
		await this.delay(this.nsStatusPropagationDelayMs);
		try {
			const entry = await firstValueFrom(
				this.quoteService.getQuoteById(Number(quoteId))
			);
			this.updateQuote(quoteId, toQuote(entry));
		} catch {
			console.error('Failed to refresh quote status');
			this.toastService.showErrorToast('Failed to refresh quote status');
		}
	}

	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	private updateQuote(quoteId: string, changes: Partial<Quote>): void {
		this.allQuotes.update((quotes) =>
			quotes.map((q) => (q.id === quoteId ? { ...q, ...changes } : q))
		);
		this.applyFilters();
	}

	public getSelectedIsr(quote: Quote): InsideSalesRep | null {
		return (
			this.insideSalesReps().find((r) => r.id === quote.insideRepId) ??
			null
		);
	}

	public getIsrTooltip(quote: Quote): string {
		if (!quote.dueDate) return 'Please enter Due Date';
		if (!quote.nsCustomerId)
			return 'NetSuite customer not matched — cannot assign yet';
		if (quote.nsEstimateId)
			return 'Reassigning will delete and recreate the NetSuite estimate';
		return '';
	}

	// What the dropdown shows: the location when the quote has one, the company
	// otherwise — see selectedNsRecordId.
	public getSelectedNsRecordId(quote: Quote): string | null {
		return selectedNsRecordId(quote);
	}

	// The column binds by id rather than by object so no per-row lookup runs on
	// every change detection pass — the customer list can be large.
	public onNsCustomerChange(
		quoteId: string,
		customerId: string | null
	): void {
		const customer =
			this.nsCustomers().find((c) => c.id === customerId) ?? null;
		this.setNsCustomer(quoteId, customer);
	}

	public setNsCustomer(quoteId: string, customer: NsCustomer | null): void {
		// Applied straight away so the dropdown reflects the click, then replaced
		// by whatever the API resolved — see nsCustomerFields.
		this.updateQuote(
			quoteId,
			nsCustomerFields(customer, this.nsCustomers())
		);
		this.persistNsCustomer(
			quoteId,
			customer?.id ?? '',
		);
	}

	// Sends only the picked record; the API derives which id is the parent company
	// and which is the location, and the response is what the row is left showing.
	private persistNsCustomer(
		quoteId: string,
		nsRecordId: string,
	): void {
		this.quoteService
			.updateNsCustomer(Number(quoteId), nsRecordId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (fields) => {
					this.updateQuote(quoteId, fields);
					this.toastService.showSuccessToast(
						'Customer updated successfully.'
					);
				},
				error: (e) => {
					this.toastService.showErrorToast("Failed to save customer")
					if (!environment.production) console.log(e);
				}
			});
	}

	public customerIsDefaultOrEmpty(quote: Quote): boolean {
		return customerIsDefaultOrEmpty(quote);
	}

	public getNsCustomerTooltip(quote: Quote): string {
		if (quote.nsEstimateId)
			return 'Estimate already created — customer can no longer be changed';
		return '';
	}

	public hoursLabel(quote: Quote): string {
		if (quote.hoursRemaining === null) return '—';
		if (quote.isOverdue)
			return `${Math.abs(quote.hoursRemaining)}h OVERDUE`;
		return `${quote.hoursRemaining}h`;
	}

	public get showEmailModalValue(): boolean {
		return this.showEmailModal();
	}

	public set showEmailModalValue(value: boolean) {
		this.showEmailModal.set(value);
		if (!value) this.selectedQuote.set(null);
	}

	public get showExportDialogValue(): boolean {
		return this.showExportDialog();
	}

	public set showExportDialogValue(value: boolean) {
		this.showExportDialog.set(value);
	}

	public openExportDialog(): void {
		this.exportStartDate = null;
		this.exportEndDate = null;
		this.showExportDialog.set(true);
	}

	public async confirmExport(): Promise<void> {
		if (!this.exportStartDate || !this.exportEndDate) {
			this.toastService.showErrorToast('Please select a start and end date');
			return;
		}
		if (this.exportEndDate < this.exportStartDate) {
			this.toastService.showErrorToast('End date must be on or after the start date');
			return;
		}

		await this.quoteLogExportService.exportQuoteLog(
			this.quotes(),
			this.exportStartDate,
			this.exportEndDate
		);
		this.showExportDialog.set(false);
	}

	public openDetailPopover(quote: Quote): void {
		if (!quote.nsEstimateId) return;
		this.selectedDetailQuote.set(quote);
		this.showDetailPopover.set(true);
		this.resetItemMatchState();

		if (!this.canMatchItems(quote)) return;

		this.loadItemMatches(Number(quote.id));
		this.loadItemOptions(Number(quote.id));
	}

	// Matching needs a NetSuite customer to resolve the item scope against, and the
	// Credit Card Sale catch-all is where unmatched quotes land — it identifies no
	// real account, so it can't tell us which item list applies.
	public canMatchItems(quote: Quote | null): boolean {
		if (!quote) return false;
		return (
			!!quote.nsCustomerId &&
			quote.nsCustomerName !== CREDIT_CARD_SALE_NAME
		);
	}

	private resetItemMatchState(): void {
		this.itemMatches.set([]);
		this.attachmentInfos.set([]);
		this.basketUnavailable.set(false);
		this.basketError.set(null);
		this.basketOptions.set([]);
		this.searchResults.set([]);
		this.searchingRowOrder.set(null);
		this.useItemSearch.set(false);
		this.isExtracting.set(false);
		this.customerNotes.set(null);
	}

	private loadItemMatches(quoteId: number): void {
		this.itemMatchesLoading.set(true);
		this.quoteService
			.getItemMatches(quoteId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (response) => {
				this.applyMatchResponse(response);

				// Nothing stored yet, so this quote has never been matched. Extraction
				// is a separate call because it costs model tokens and writes rows.
				if (response.needsExtraction && !response.basketUnavailable) {
					this.runExtraction(quoteId, false);
					return;
				}

				this.itemMatchesLoading.set(false);
			},
			error: () => {
				this.itemMatchesLoading.set(false);
				this.toastService.showErrorToast('Failed to load item matches');
			}
		});
	}

	private runExtraction(quoteId: number, refresh: boolean): void {
		this.itemMatchesLoading.set(true);
		this.isExtracting.set(true);
		this.quoteService
			.extractItemMatches(quoteId, refresh)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (response) => {
				this.applyMatchResponse(response);
				this.itemMatchesLoading.set(false);
				this.isExtracting.set(false);

				// A failed extraction still comes back as a 200 with the prior rows, so it
				// wouldn't otherwise reach the error handler below.
				if (response.extractionFailed) {
					this.toastService.showErrorToast(
						response.extractionError ??
							'Failed to read line items from this quote'
					);
				}
			},
			error: (e) => {
				this.itemMatchesLoading.set(false);
				this.isExtracting.set(false);
				this.toastService.showErrorToast('Failed to read line items from this quote');
				if (!environment.production) {console.log(e)}
			}
		});
	}

	// Re-runs extraction. Rows the user overrode or added by hand are preserved by
	// the API, so this is safe to offer without a confirmation.
	public refreshItemMatches(): void {
		const quote = this.selectedDetailQuote();
		if (!quote || !this.canMatchItems(quote)) return;
		this.runExtraction(Number(quote.id), true);
	}

	private applyMatchResponse(response: {
		rows: QuoteItemMatch[];
		sourceList: string;
		basketUnavailable: boolean;
		basketError: string | null;
		attachments: AttachmentExtractionInfo[];
		lowConfidenceThreshold: number;
		customerNotes: string | null;
	}): void {
		this.itemMatches.set(response.rows);
		this.customerNotes.set(response.customerNotes);
		this.matchSourceList.set(response.sourceList);
		this.basketUnavailable.set(response.basketUnavailable);
		this.basketError.set(response.basketError);
		this.lowConfidenceThreshold.set(response.lowConfidenceThreshold);
		// Only overwrite on a response that carries them: the read endpoint doesn't
		// report attachment outcomes, and blanking them would lose the extract's report.
		if (response.attachments.length > 0) {
			this.attachmentInfos.set(response.attachments);
		}
	}

	private loadItemOptions(quoteId: number): void {
		this.quoteService
			.getItemOptions(quoteId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: (response) => {
				this.useItemSearch.set(response.useSearch);
				this.basketOptions.set(response.options);
			},
			error: () => {
				// Fall back to search rather than leaving the dropdown empty.
				this.useItemSearch.set(true);
			}
		});
	}

	// The options a given row's dropdown shows. The currently selected item is always
	// merged in: with server-side search it usually isn't in the filtered results, and
	// PrimeNG renders a blank cell for a value it can't find among the options.
	public optionsForRow(row: QuoteItemMatch): ItemOption[] {
		const base = this.useItemSearch()
			? this.searchingRowOrder() === row.displayOrder
				? this.searchResults()
				: []
			: this.basketOptions();

		if (!row.nsInternalId) return base;
		if (base.some((o) => o.nsInternalId === row.nsInternalId)) return base;

		return [
			{
				nsInternalId: row.nsInternalId,
				itemId: row.nsItemId,
				description: row.nsDescription,
				quantityAvailable: null
			},
			...base
		];
	}

	public onItemFilter(row: QuoteItemMatch, query: string): void {
		if (!this.useItemSearch()) return;
		const quote = this.selectedDetailQuote();
		if (!quote) return;

		this.searchingRowOrder.set(row.displayOrder);
		this.itemSearch$.next({ quoteId: Number(quote.id), query });
	}

	public onPartCodeChange(row: QuoteItemMatch, nsInternalId: string): void {
		const option = this.optionsForRow(row).find(
			(o) => o.nsInternalId === nsInternalId
		);

		this.updateMatchRow(row.displayOrder, {
			nsInternalId,
			nsItemId: option?.itemId ?? null,
			nsDescription: option?.description ?? null,
			matchFound: true,
			// The score described the item the matcher chose, not this one, so it stops
			// being meaningful the moment the user picks something else.
			isUserOverridden: true,
			originalConfidence: null
		});
	}

	public onMarkNumberChange(row: QuoteItemMatch, markNumber: string): void {
		this.updateMatchRow(row.displayOrder, { markNumber });
	}

	public onQuantityChange(row: QuoteItemMatch, quantity: string): void {
		this.updateMatchRow(row.displayOrder, { quantity });
	}

	private updateMatchRow(
		displayOrder: number,
		changes: Partial<QuoteItemMatch>
	): void {
		this.itemMatches.update((rows) =>
			rows.map((r) =>
				r.displayOrder === displayOrder ? { ...r, ...changes } : r
			)
		);
	}

	public deleteMatchRow(row: QuoteItemMatch): void {
		this.itemMatches.update((rows) =>
			rows
				.filter((r) => r.displayOrder !== row.displayOrder)
				// Mark number is the item's position, so it stays contiguous. This does
				// overwrite a hand-typed mark on the rows below the deletion.
				.map((r, i) => ({
					...r,
					displayOrder: i,
					markNumber: String(i + 1)
				}))
		);
	}

	// For a line extraction missed entirely — the trashcan alone can't fix that.
	public addMatchRow(): void {
		this.itemMatches.update((rows) => [
			...rows,
			{
				id: 0,
				displayOrder: rows.length,
				lineIndex: null,
				markNumber: String(rows.length + 1),
				quantity: null,
				unitOfMeasure: null,
				customerDescription: null,
				nsInternalId: null,
				nsItemId: null,
				nsDescription: null,
				originalConfidence: null,
				matchFound: false,
				isUserOverridden: false,
				isManuallyAdded: true,
				isSuspectedDuplicate: false,
				sourceOrigin: 'manual',
				sourceFileName: null,
				sourceList: this.matchSourceList()
			}
		]);
	}

	// Saves through the API first, then writes the sheet. The decision record has to
	// land before the file leaves, so a failed save must not produce a download.
	public async exportItemMatches(): Promise<void> {
		const quote = this.selectedDetailQuote();
		if (!quote || this.isExporting()) return;

		this.isExporting.set(true);
		try {
			const saved = await firstValueFrom(
				this.quoteService.saveItemMatches(
					Number(quote.id),
					this.itemMatches().map((r) => this.toSaveRow(r))
				)
			);

			this.applyMatchResponse(saved);
			this.writeItemMatchWorkbook(quote, saved.rows);
			this.toastService.showSuccessToast(
				'Item matches saved and exported'
			);
		} catch {
			this.toastService.showErrorToast(
				'Failed to save item matches — nothing was exported'
			);
		} finally {
			this.isExporting.set(false);
		}
	}

	private toSaveRow(row: QuoteItemMatch): QuoteItemMatchSaveRow {
		return {
			displayOrder: row.displayOrder,
			lineIndex: row.lineIndex,
			markNumber: row.markNumber,
			quantity: row.quantity,
			unitOfMeasure: row.unitOfMeasure,
			customerDescription: row.customerDescription,
			rawLine: null,
			nsInternalId: row.nsInternalId,
			isManuallyAdded: row.isManuallyAdded,
			sourceOrigin: row.sourceOrigin,
			sourceFileName: row.sourceFileName
		};
	}

	private writeItemMatchWorkbook(quote: Quote, rows: QuoteItemMatch[]): void {
		const sheetRows = [...rows]
			// Sort on displayOrder, never the user-editable mark number.
			.sort((a, b) => a.displayOrder - b.displayOrder)
			.map((r) => ({
				'Mark Number': this.excelSafe(r.markNumber),
				Quantity: this.excelSafe(r.quantity),
				'RK Part Code': this.excelSafe(r.nsItemId),
				'NS RK Description': this.excelSafe(r.nsDescription),
				'Customer Description': this.excelSafe(r.customerDescription),
				'Confidence Score': r.isUserOverridden
					? 'Manual'
					: (r.originalConfidence ?? '')
			}));

		const ws = XLSX.utils.json_to_sheet(sheetRows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Item Matches');
		XLSX.writeFile(wb, `quote-${quote.id}-item-matches.xlsx`);
	}

	// Excel treats a leading =, +, - or @ as a formula, and scraped descriptions can
	// legitimately start with any of them. Prefixing with an apostrophe keeps the cell text.
	private excelSafe(value: string | null): string {
		if (!value) return '';
		return /^[=+\-@]/.test(value) ? `'${value}` : value;
	}

	public confidenceLabel(row: QuoteItemMatch): string {
		if (row.isUserOverridden || row.isManuallyAdded) return '—';
		if (!row.matchFound || row.originalConfidence === null) return '—';
		return `${Math.round(row.originalConfidence)}%`;
	}

	public isLowConfidence(row: QuoteItemMatch): boolean {
		return (
			row.matchFound &&
			!row.isUserOverridden &&
			row.originalConfidence !== null &&
			row.originalConfidence < this.lowConfidenceThreshold()
		);
	}

	public attachmentSeverity(
		status: AttachmentExtractionInfo['status']
	): 'success' | 'warn' | 'danger' | 'secondary' {
		switch (status) {
			case 'Parsed':
				return 'success';
			case 'Truncated':
				return 'warn';
			case 'Failed':
				return 'danger';
			default:
				return 'secondary';
		}
	}

	// Only the outcomes worth the user's attention; a fully parsed set says nothing new.
	public problemAttachments = computed(() =>
		this.attachmentInfos().filter((a) => a.status !== 'Parsed')
	);

	public get showDetailPopoverValue(): boolean {
		return this.showDetailPopover();
	}

	public set showDetailPopoverValue(value: boolean) {
		this.showDetailPopover.set(value);
		if (!value) this.selectedDetailQuote.set(null);
	}


	public downloadAttachment(attachment: Attachment): void {
		const byteChars = atob(attachment.contentBytes);
		const byteArray = new Uint8Array(byteChars.length);
		for (let i = 0; i < byteChars.length; i++) {
			byteArray[i] = byteChars.charCodeAt(i);
		}
		const blob = new Blob([byteArray], { type: attachment.contentType });
		const url = URL.createObjectURL(blob);

		if (attachment.contentType === 'application/pdf') {
			window.open(url, '_blank');
		} else {
			const a = document.createElement('a');
			a.href = url;
			a.download = attachment.name;
			a.click();
			URL.revokeObjectURL(url);
		}
	}

	public syncStatuses(): void {
		this.isSyncing.set(true);
		this.quoteService
			.syncStatuses()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: () => {
				this.quoteService
					.getQuoteLog()
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
					next: (entries) => {
						this.allQuotes.set(entries.map((e) => toQuote(e)));
						this.applyFilters();
						this.isSyncing.set(false);
					},
					error: () => {
						this.isSyncing.set(false);
						console.error('Failed to load quote log');
						this.toastService.showErrorToast(
							'Failed to load quotes'
						);
					}
				});
			},
			error: (e) => {
				this.isSyncing.set(false);
				this.toastService.showErrorToast('Failed to refresh NetSuite statuses');
				if (!environment.production) {console.log(e)}
			}
		});
	}

	public openDueDatePicker(quote: Quote): void {
		if (quote.insideRepId && quote.nsEstimateId) return;
		this.editingDueDateId.set(quote.id);
		const original = quote.dueDate ? new Date(toIsoUtc(quote.dueDate)) : null;
		// this.editingDueDateOriginal = original;
		this.editingDueDateValue.set(original);
	}

	public saveDueDate(picker: DatePicker, quote: Quote): void {
		picker.hideOverlay();

		const val = picker.value as Date | null;
		if (val) val.setSeconds(0, 0);
		const original = quote.dueDate ? new Date(toIsoUtc(quote.dueDate)) : null;
		if (original) original.setSeconds(0, 0);

		const unchanged =
			val === null
				? original === null
				: original !== null && val.getTime() === original.getTime();
		if (unchanged) return;

		const isoString = val ? val.toISOString() : null;

		this.updateQuote(quote.id, {
			dueDate: isoString,
			dueLabel: isoString ? new Date(isoString).toLocaleString() : '—',
			hoursRemaining: calcHours(isoString),
			isOverdue: calcIsOverdue(isoString),
			dueDateIsoUtc: val
		});

		this.toastService.showLoadingToast('Updating due date...', true);
		this.quoteService
			.updateDueDate(Number(quote.id), isoString)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => {
					this.toastService.clearStickyToasts();
				})
			)
			.subscribe({
				next: () => {
					this.toastService.showSuccessToast('Due date updated');
				},
				error: (e) => {
					this.toastService.showErrorToast('Failed to save due date');
					if (!environment.production) console.log(e);
				}
			});
	}

	private getQuoteThreadReplies(quote: Quote): void {
		this.quoteService
			.getQuoteThreadReplies(quote)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (replies: ThreadReply[]) => {
					this.selectedQuote.update((current) =>
						current?.id === quote.id
							? { ...current, threadReplies: replies }
							: current
					);
				},
				error: (e) => {
					this.toastService.showErrorToast(
						'Error retrieving quote thread replies'
					);
					if(!environment.production) console.log(e);
				}
			});
	}

	public nextCardPage(): void {
		if (this.currentCardPage() < this.totalCardPages() - 1) {
			this.currentCardPage.update((page) => page + 1);
		}
	}

	public previousCardPage(): void {
		if (this.currentCardPage() > 0) {
			this.currentCardPage.update((page) => page - 1);
		}
	}

	public goToCardPage(page: number): void {
		if (page >= 0 && page < this.totalCardPages()) {
			this.currentCardPage.set(page);
		}
	}

	public confirmDeleteQuote(quote: Quote, event?: Event): void {
		event?.stopPropagation();
		this.confirmationService.confirm({
			key: 'deleteQuote',
			header: 'Delete Quote',
			message: `Are you sure you want to delete the quote from ${quote.customer}? This cannot be undone.`,
			icon: 'pi pi-exclamation-triangle',
			acceptButtonStyleClass: 'p-button-danger',
			acceptLabel: 'Delete',
			rejectLabel: 'Cancel',
			accept: () => {
				this.deleteQuote(quote);
			}
		});
	}

	private deleteQuote(quote: Quote): void {
		this.quoteService
			.deleteQuoteLogEntries([Number(quote.id)])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
			next: () => {
				this.allQuotes.update((quotes) =>
					quotes.filter((q) => q.id !== quote.id)
				);
				this.applyFilters();
				this.toastService.showSuccessToast('Quote deleted');
			},
			error: (e) => {
				this.toastService.showErrorToast('Failed to delete quote');
				if (!environment.production) console.log(e);
			}
		});
	}
}
